/** @format */

import React, { useEffect, useState } from "react";
import { Nav, Row, Tab } from "react-bootstrap";
import styled from "styled-components";
import Button from "../Atoms/Button/Button";
import { SelectCallback } from "@restart/ui/types";
import { random } from "lodash";
import { ProfileTabs } from "../../Pages/Profile";
import { isV1 } from "../App/App";

type ImageTab = {
  component: React.ReactNode;
  eventKey: string;
  label?: string;
  icon: React.ReactNode;
};

type ImageTabsProps = {
  tabs: ImageTab[];
  chosenByDefault?: string | undefined;
  handleSelect: SelectCallback;
};

// Hexagon wrapper for the glowing border effect
const HexagonOuter = styled.div<{ isActive?: boolean }>`
  position: relative;
  width: 60px;
  height: 68px;
  cursor: pointer;
  filter: ${props => props.isActive
    ? 'drop-shadow(0 0 8px rgba(0, 210, 255, 0.8)) drop-shadow(0 0 12px rgba(138, 43, 226, 0.6))'
    : 'drop-shadow(0 0 5px rgba(138, 43, 226, 0.5))'};
  transition: filter 0.3s ease, transform 0.2s ease;
  
  &:hover {
    filter: drop-shadow(0 0 10px rgba(0, 210, 255, 0.9)) drop-shadow(0 0 15px rgba(138, 43, 226, 0.7));
    transform: scale(1.05);
  }
  
  /* Tablet screens */
  @media (min-width: 768px) {
    width: 58px;
    height: 75px;
  }
  
  /* Desktop screens */
  @media (min-width: 992px) {
    width: 68px;
    height: 77px;
  }
  
  /* Large desktop screens */
  @media (min-width: 1200px) {
    width: 72px;
    height: 82px;
  }
`;

// The glowing border hexagon
const HexagonBorder = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, #00D4FF 0%, #8A2BE2 50%, #4B0082 100%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
`;

// The inner hexagon with gradient background
const HexagonInner = styled.div`
  position: absolute;
  top: 2px;
  left: 2px;
  right: 2px;
  bottom: 2px;
  background: linear-gradient(180deg, #6B4DE6 0%, #4A2B9E 40%, #2D1B69 100%);
  clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 5px 3px;
  
  @media (min-width: 768px) {
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    padding: 6px 4px;
  }
  
  @media (min-width: 992px) {
    top: 3px;
    left: 3px;
    right: 3px;
    bottom: 3px;
    padding: 7px 5px;
  }
`;

const IconWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 2px;
  
  svg {
    width: 18px;
    height: 18px;
    fill: white;
    
    path {
      fill: white;
    }
    
    @media (min-width: 768px) {
      width: 20px;
      height: 20px;
    }
    
    @media (min-width: 992px) {
      width: 22px;
      height: 22px;
    }
    
    @media (min-width: 1200px) {
      width: 24px;
      height: 24px;
    }
  }
  
  @media (min-width: 768px) {
    margin-bottom: 2px;
  }
  
  @media (min-width: 992px) {
    margin-bottom: 3px;
  }
`;

const Label = styled.div`
  font-family: Poppins, sans-serif;
  font-size: 6px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: white;
  text-align: center;
  text-shadow: 0px 1px 2px rgba(91, 3, 255, 0.5);
  line-height: 1.1;
  
  @media (min-width: 768px) {
    font-size: 6px;
    letter-spacing: 0.4px;
  }
  
  @media (min-width: 992px) {
    font-size: 7px;
    letter-spacing: 0.5px;
  }
  
  @media (min-width: 1200px) {
    font-size: 8px;
    letter-spacing: 0.5px;
  }
`;

const TabsGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 12px 10px;
  padding: 15px 10px;
  background: linear-gradient(180deg, #1a0a35 0%, #0d0520 100%);
  overflow: visible;
  width: 100vw;
  margin-left: calc(-50vw + 50%);
  
  /* Mobile: 4 items per row */
  & > * {
    flex: 0 0 calc(25% - 10px);
    max-width: calc(25% - 10px);
    display: flex;
    justify-content: center;
  }
  
  @media (min-width: 768px) {
    gap: 14px 12px;
    padding: 20px 15px;
    
    & > * {
      flex: 0 0 auto;
      max-width: none;
    }
  }
  
  @media (min-width: 992px) {
    gap: 14px;
    padding: 25px 20px;
  }
  
  @media (min-width: 1200px) {
    gap: 16px;
    padding: 25px 30px;
  }
`;

type Props = { iconName?: any };
const NavLink = styled(Nav.Link)`
  padding: 0 !important;
  background: transparent !important;
  
  &.active {
    background: transparent !important;
  }

  &:hover, &:focus {
    background: transparent !important;
  }
`;


const ImageTabs = ({ tabs, chosenByDefault, handleSelect }: ImageTabsProps) => {

  const [activeValue, setActiveValue] = useState("");

  useEffect(() => {
    activeTab(tabs, chosenByDefault);
  }, [chosenByDefault]);


  const activeTab = (tabs: any, chosenByDefault: any) => {
    tabs.map((tab: any, index: number) => {
      if (chosenByDefault?.includes(tab.eventKey)) {
        setActiveValue(tabs[index].eventKey);
      }
    });
  };

  // Handle tab click - update active state and call the handleSelect callback
  const onTabClick = (eventKey: string) => {
    setActiveValue(eventKey);
    if (handleSelect) {
      handleSelect(eventKey, {} as React.SyntheticEvent);
    }
  };

  return (
    <Tab.Container
      id={`image-tabs-${random(100)}`}
      activeKey={activeValue}
    >
      <TabsGrid>
        {tabs.map((tab, i) => {
          const isActive = activeValue === tab.eventKey;
          return (
            <Nav.Item key={i}>
              <HexagonOuter
                isActive={isActive}
                onClick={() => onTabClick(tab.eventKey)}
              >
                <HexagonBorder />
                <HexagonInner>
                  <IconWrapper>
                    {tab.icon}
                  </IconWrapper>
                  {tab.label && (
                    <Label>
                      {tab.label === "CollectiblesAlbum" ? "ALBUM" : tab.label.toUpperCase()}
                    </Label>
                  )}
                </HexagonInner>
              </HexagonOuter>
            </Nav.Item>
          );
        })}
      </TabsGrid>
      <Row>
        <Tab.Content>
          {tabs.map((tab, i) => {
            return (
              <Tab.Pane key={i} eventKey={tab.eventKey}>
                {tab.component}
              </Tab.Pane>
            );
          })}
        </Tab.Content>
      </Row>
    </Tab.Container>
  );
};

export default ImageTabs;
